// externals
import { connect } from 'react-redux';
import { reduxForm, formValueSelector } from 'redux-form';

// interfaces/types
import { IConfigLeadform } from '../../types/IConfigLeadform';

// selectors
import { Selectors } from '../../store/selectors';

// actions
import * as leadFormActionCreators from '../../store/actionCreators';

// utils
import validate from './validate';
import { cloneDeep, isEmpty, assignIn } from '../../utils';

// components
import LeadFormUI from './LeadFormUI';

// consts/enums
import { ILeadFormConstants, RouteValues } from './LeadFormConstants';

export const messageNormalizer = (value, _previousValue) => {
    return value.length > 500 ? value.slice(0, 500) : value;
};

export interface ILeadFormProps {
    hidePhoneNumber?: boolean;
    hideContactPreffered?: boolean;
    hideAdditionalInfo?: boolean;
    isLeadFormLite?: boolean;
    hideTitle?: boolean;
    hideNextStepsLink?: boolean;
    isLeadFormShort?: boolean;
    hideUserConsent?: boolean;
    isLeadSummary?: boolean;
    offerHasBeenSent?: boolean;
    isSingleColumn?: boolean;
    isLeadFormSingleCol?: boolean;
    selector: Selectors;
    config: IConfigLeadform;
    zipCode: string;
}

export interface ILeadFormUIProps {
    initialValues: any;
    offerHasBeenSent: boolean;
    offerSubmitting: boolean;
    offerSubmitFailed: boolean;
    shopperConsentText?: string;
    currentShopperInfo?: any;
    leadFormFieldState?: any;
    messageValue?: string;
    maxMessageLength?: number;
    messageNormalizer: { (value: string, previousValue: string): string };
    isTcpaEnabled: boolean;
    isCCPAEnabled: boolean;
    CCPAConsentText: boolean;
    isTCPACheckboxHidden: boolean;
    isPhoneNumberRequired: boolean;
    isLeadFormLite: boolean;
    hidePhoneNumber?: boolean;
    hideContactPreffered?: boolean;
    hideAdditionalInfo?: boolean;
    hideTitle?: boolean;
    hideNextStepsLink?: boolean;
    hideUserConsent?: boolean;
    isLeadFormShort?: boolean;
    showSocialOAuth?: boolean;
    leadFormConstants?: any;
    isSingleColumn?: boolean;
    isLeadFormSingleCol?: boolean;
}

interface IContactPreferences {
    email?: boolean;
    phone?: boolean;
    sms?: boolean;
}

interface ICurrentShopperInfo {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    message?: string;
    contactPreferences?: IContactPreferences;
}

let currentShopperInfo: ICurrentShopperInfo = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    contactPreferences: {
        email: false,
        phone: false,
        sms: false
    }
};

const getShopperInfo = () => {
    if (isEmpty(currentShopperInfo)) {
        return { shopper: { zip: '' }, message: '' };
    }

    const values = {
        shopper: {
            firstName: currentShopperInfo.firstName || '',
            lastName: currentShopperInfo.lastName || '',
            email: currentShopperInfo.email || '',
            phone: currentShopperInfo.phone || '',
            contactPreferences: {
                email: !!currentShopperInfo?.contactPreferences?.email || false,
                phone: !!currentShopperInfo?.contactPreferences?.phone || false,
                sms: !!currentShopperInfo?.contactPreferences?.sms || false
            }
        },
        message: currentShopperInfo.message || ''
    };

    return values;
};

const isChecked = (state): boolean => {
    const {
        offer: { shopper }
    } = state;
    return (
        shopper &&
        shopper.contactPreferences &&
        (shopper.contactPreferences.email || shopper.contactPreferences.phone || shopper.contactPreferences.sms)
    );
};

const mapStateToProps = (state: any, ownProps: ILeadFormProps) => {
    const offerInfoSelectors = ownProps.selector.additionalSelectors.offerInfoSelectors;
    const dealerSelectors = ownProps.selector.additionalSelectors.dealerSelectors;
    const leadFormInfoSelectors = ownProps.selector.additionalSelectors.leadFormInfoSelectors;
    const isTCPACheckboxHidden = dealerSelectors.getTCPACheckboxStatus(state);
    const initialValues = state.offer || { shopper: { firstName: '', userConsent: 'unchecked' }, message: '' };

    // set default preferred method of communication to email
    if (!isChecked(state)) {
        // TODO: In future this code should be changed to avoid using "extend"
        assignIn(initialValues.shopper, { contactPreferences: { email: true } });
    }
    if (isTCPACheckboxHidden) {
        initialValues.shopper.userConsent = 'checked';
    }

    currentShopperInfo = state.form.leadForm
        ? assignIn(state.form.leadForm.values.shopper, { message: state.form.leadForm.values.message })
        : {};
    const offerHasBeenSent = offerInfoSelectors.getHasBeenSent(state);
    const offerSubmitting = leadFormInfoSelectors.isOfferSubmitting(state);
    const offerSubmitFailed = leadFormInfoSelectors.isOfferSubmitFailed(state);
    const shopperConsentText = dealerSelectors.getShopperConsentText(state);
    const isTcpaEnabled = dealerSelectors.getTCPAEnabled(state);
    const isCCPAEnabled = dealerSelectors.getCCPAEnabled(state);
    const isPhoneNumberRequired = dealerSelectors.getPhoneNumberRequiredEnabled(state);
    const CCPAConsentText = dealerSelectors.getCCPAConsentText(state);
    const dealerZip = dealerSelectors.getDealerZip(state);
    const leadFormValuesSelector = formValueSelector('leadForm');
    const leadFormFieldState =
        state.form.leadForm && state.form.leadForm.fields ? assignIn(state.form.leadForm.fields.shopper) : {};
    const messageValue = leadFormValuesSelector(state, 'message');
    const maxMessageLength = 500;

    const leadFormConstants: ILeadFormConstants = {
        pageTitle: '',
        heading: '',
        subHeading: ''
    };

    const contactPhone = leadFormValuesSelector(state, 'shopper.contactPreferences.phone');
    const contactText = leadFormValuesSelector(state, 'shopper.contactPreferences.sms');
    const hideUserConsent = !ownProps.hideUserConsent && (contactPhone || contactText) ? false : true;
    const leadFormCCPAShown = ownProps.selector.isLeadFormCCPAShown(state);
    const zipCodeLoading = ownProps.selector.getZipCodeLoading(state);
    const result: any = {
        initialValues,
        isLeadFormLite: ownProps.isLeadFormLite || false,
        offerHasBeenSent,
        offerSubmitting,
        offerSubmitFailed,
        shopperConsentText,
        messageValue,
        messageNormalizer,
        maxMessageLength,
        currentShopperInfo, // helps to properly track contactPreferences state,
        leadFormFieldState,
        isTcpaEnabled,
        isTCPACheckboxHidden,
        isCCPAEnabled,
        CCPAConsentText,
        dealerZip,
        isLeadFormShort: ownProps.isLeadFormShort || false,
        hidePhoneNumber: ownProps.hidePhoneNumber || false,
        hideContactPreffered: ownProps.hideContactPreffered || false,
        hideAdditionalInfo: ownProps.hideAdditionalInfo || false,
        hideTitle: ownProps.hideTitle || false,
        hideNextStepsLink: ownProps.hideNextStepsLink || false,
        hideUserConsent,
        leadFormCCPAShown,
        leadFormConstants,
        isSingleColumn: ownProps.isSingleColumn || false,
        isLeadFormSingleCol: ownProps.isLeadFormSingleCol || false,
        isPhoneNumberRequired,
        zipCodeLoading
    };
    return result;
};

const mapDispatchToProps = (dispatch, ownProps: ILeadFormProps) => {
    const offerActionCreators = ownProps.config.offerActionCreators;
    const { submitOffer } = offerActionCreators;
    const { goToNext } = ownProps.config.routingActionCreators;
    const { onFormSubmit, onFirstNameBlur, onLastNameBlur, onEmailBlur, onPhoneBlur, onMessageBlur, uspsZipInitialize } =
        leadFormActionCreators;

    return {
        onSubmit: (values) => {
            delete values.shopper.zip;
            const clonedValues = cloneDeep(values);

            if (!values.shopper.contactPreferences.phone && !values.shopper.contactPreferences.sms) {
                clonedValues.shopper.userConsent = 'unchecked';
            }

            dispatch(offerActionCreators.updatedShopper(clonedValues));
            dispatch(onFormSubmit());
            dispatch(ownProps.config.leadFormActionCreators.updateCommunicationPreference());
            ownProps.config.leadFormActionCreators.checkMessageOnFormSubmit(clonedValues, dispatch);
            dispatch(ownProps.config.leadFormActionCreators.checkZipOnFormSubmit(ownProps.zipCode));
            return dispatch(submitOffer(true));
        },

        constructErrorMessage: (error, submitFailed, offerSubmitFailed) => {
            if (error && submitFailed) {
                return error;
            }

            const message = offerSubmitFailed ? 'Unable to submit offer' : '';

            return message;
        },

        onFirstNameBlur: (reportAnalytics: boolean) => {
            dispatch((dispatch, getState) => {
                const state = getState();
                const location = state.router.location.pathname;
                dispatch(offerActionCreators.updatedShopper(getShopperInfo()));
                if (reportAnalytics) {
                    if (location === '/tradeLeadForm') {
                        dispatch(ownProps.config.leadFormActionCreators.onTradeInFirstNameBlur());
                    } else {
                        dispatch(onFirstNameBlur());
                    }
                }
            });
        },
        onLastNameBlur: (reportAnalytics: boolean) => {
            dispatch((dispatch, getState) => {
                const state = getState();
                const location = state.router.location.pathname;
                dispatch(offerActionCreators.updatedShopper(getShopperInfo()));
                if (reportAnalytics) {
                    if (location === '/tradeLeadForm') {
                        dispatch(ownProps.config.leadFormActionCreators.onTradeInLastNameBlur());
                    } else {
                        dispatch(onLastNameBlur());
                    }
                }
            });
        },
        onEmailBlur: (reportAnalytics: boolean) => {
            dispatch((dispatch, getState) => {
                const state = getState();
                const location = state.router.location.pathname;
                dispatch(offerActionCreators.updatedShopper(getShopperInfo()));
                if (reportAnalytics) {
                    if (location === '/tradeLeadForm') {
                        dispatch(ownProps.config.leadFormActionCreators.onTradeInEmailBlur());
                    } else {
                        dispatch(onEmailBlur());
                    }
                }
            });
        },
        onPhoneBlur: (reportAnalytics: boolean) => {
            dispatch((dispatch, getState) => {
                const state = getState();
                const location = state.router.location.pathname;
                dispatch(offerActionCreators.updatedShopper(getShopperInfo()));
                if (reportAnalytics) {
                    if (location === '/tradeLeadForm') {
                        dispatch(ownProps.config.leadFormActionCreators.onTradeInPhoneBlur());
                    } else {
                        dispatch(onPhoneBlur());
                    }
                }
            });
        },
        onCommunicationPreferenceChange: () => {
            dispatch(offerActionCreators.updatedShopper(getShopperInfo()));
        },
        onMessageBlur: (reportAnalytics: boolean) => {
            dispatch(offerActionCreators.updatedShopper(getShopperInfo()));
            if (reportAnalytics) {
                dispatch(onMessageBlur());
            }
        },
        onContinueToNextStepClick: () => {
            // only used on lead form lite/lead summary page and goToNext needs to know what the current step
            // in the flow the user is on but this is before any flow is entered so we're just providing pencil
            // so that it goes to the first incomplete flow
            dispatch(goToNext(RouteValues.TERMS));
        },
        uspsZipInitialize: () => {
            dispatch(uspsZipInitialize());
        }
    };
};

const LeadForm = reduxForm<any, ILeadFormUIProps>({
    form: 'leadForm',
    validate,
    destroyOnUnmount: false,
    // Needed to add touchOnChange: true in order to get the analytic events to fire correctly.
    touchOnChange: true
    // TODO: leaving here for education purposes, v7.4.2 had changes
    // https://github.com/erikras/redux-form/commit/fdb92f519030bb26a505ae7da6638a51ae103812
    // enableReinitialize: false,
    // keepDirtyOnReinitialize: false
})(LeadFormUI as any);

export default connect(mapStateToProps, mapDispatchToProps)(LeadForm);
